import {useAuthStore} from '@features/user/store/store.ts'
import {useState} from 'react'
import {useNavigate} from 'react-router'
import {Trans, useTranslation, TKey} from '@/translations/i18n.tsx'
import {Assignment, ServiceType} from '@features/assignment/types.ts'
import {formatLocaleDate, parseDate} from '@/dates.ts'
import {constructNow, differenceInDays, differenceInHours} from 'date-fns'
import {Button} from '@components/ui/button.tsx'
import {CheckIcon, XCloseIcon} from '@components/ui/icon.tsx'
import {Spinner} from '@components/ui/spinner.tsx'
import {DeclineAssignmentModal} from '@features/assignment/components/decline-assignment-modal'
import {routes} from '@utilities/constants/routes.tsx'
import {isPastInListingTimeZone, sortHostAvailabilityDates} from '@features/assignment/utils.tsx'
import {useAcceptAssignment} from '@features/assignment/queries/useAcceptAssignment.ts'
import {cn} from '@utilities/helpers'
import {
    ASSIGNMENT_DESCRIPTION_PARAGRAPH_CLASSNAME,
    ASSIGNMENT_DESCRIPTION_TEXT_CLASSNAME,
    ASSIGNMENT_DESCRIPTION_TITLE_CLASSNAME
} from '@features/assignment/constants'

const getExpirationDifference = (expirationDate: string): {unit: 'day' | 'hour'; value: number} => {
    const expiration = parseDate(expirationDate)
    const now = constructNow(expiration)
    const expirationDifferenceInHours = differenceInHours(expiration, now)
    const expirationDifferenceInDays = differenceInDays(expiration, now)

    if (expirationDifferenceInHours > 24) {
        return {
            unit: 'day',
            value: expirationDifferenceInDays >= 0 ? expirationDifferenceInDays : 0
        }
    }

    return {
        unit: 'hour',
        value: expirationDifferenceInHours >= 0 ? expirationDifferenceInHours : 0
    }
}

const formatDateCardTitle = (startingTime: string) => formatLocaleDate(startingTime, 'EEEE, MMM dd, yyyy')
const formatDateCardSubtitle = (startingTime: string, endingTime: string) =>
    `${formatLocaleDate(startingTime, 'p')} – ${formatLocaleDate(endingTime, 'p')}`

export const FirstAcceptPendingDescription = ({
    assignment,
    serviceType
}: {
    assignment: Assignment
    serviceType: ServiceType
}) => {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const [selectedDate, setSelectedDate] = useState<string | null>(null)
    const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false)
    const acceptAssignmentMutation = useAcceptAssignment(assignment)
    const expirationDifference = assignment.expiration_date ? getExpirationDifference(assignment.expiration_date) : null
    const adminAccessToken = useAuthStore(state => state.adminAccessToken)
    const dates = sortHostAvailabilityDates(assignment.quote?.host_availability_dates ?? [])

    return (
        <div className="flex flex-col gap-8 md:gap-6">
            <div className={ASSIGNMENT_DESCRIPTION_TEXT_CLASSNAME}>
                <h3 className={ASSIGNMENT_DESCRIPTION_TITLE_CLASSNAME}>{t('assignment:make_a_decision')}</h3>
                <p className={ASSIGNMENT_DESCRIPTION_PARAGRAPH_CLASSNAME}>
                    <Trans
                        i18nKey={`assignment:first_accept:paragraph:${serviceType}` as TKey}
                        components={{strong: <strong />}}
                        values={{
                            difference: t(
                                expirationDifference?.unit === 'day' ? 'commons:x_days' : 'assignment:x_hours',
                                {
                                    count: expirationDifference?.value ?? 0
                                }
                            )
                        }}
                    />
                </p>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 lg:grid-cols-4">
                {dates.map(date => {
                    const isSelected = selectedDate === date.starting_time
                    const isPastDate =
                        !adminAccessToken &&
                        isPastInListingTimeZone(parseDate(date.starting_time), assignment.entity.timezone)
                    return (
                        <button
                            key={date.starting_time}
                            type="button"
                            className={cn(
                                'flex cursor-pointer flex-col gap-1 rounded-xl bg-white text-left transition-[border-color,background-color,box-shadow] duration-200 ease-in-out disabled:cursor-not-allowed disabled:opacity-60 [&_.date-card-subtitle]:text-sm [&_.date-card-subtitle]:text-neutral-500 [&_.date-card-title]:text-md [&_.date-card-title]:font-semibold [&_.date-card-title]:text-neutral-900',
                                isSelected
                                    ? 'border-2 border-neutral-900 px-[19px] py-[15px]'
                                    : 'border border-neutral-200 px-5 py-4 hover:not-disabled:border-neutral-400'
                            )}
                            disabled={acceptAssignmentMutation.isPending || isPastDate}
                            onClick={() => setSelectedDate(date.starting_time)}
                            aria-pressed={isSelected}
                        >
                            <span className="date-card-title">{formatDateCardTitle(date.starting_time)}</span>
                            <span className="date-card-subtitle">
                                {formatDateCardSubtitle(date.starting_time, date.ending_time)}
                            </span>
                        </button>
                    )
                })}
            </div>

            <div className="flex w-full flex-col justify-end gap-2 md:w-auto md:flex-row">
                <Button
                    variant="primarySuccess"
                    onClick={() => selectedDate && acceptAssignmentMutation.mutate(selectedDate)}
                    disabled={!selectedDate || acceptAssignmentMutation.isPending}
                >
                    <CheckIcon />
                    {t('assignment:first_accept:accept_and_schedule')}
                    {acceptAssignmentMutation.isPending && <Spinner size={20} />}
                </Button>
                <Button
                    variant="primaryDanger"
                    onClick={() => setIsDeclineModalOpen(true)}
                    disabled={acceptAssignmentMutation.isPending}
                >
                    <XCloseIcon />
                    {t('commons:decline')}
                </Button>
            </div>

            {isDeclineModalOpen && (
                <DeclineAssignmentModal
                    assignment={assignment}
                    onClose={() => setIsDeclineModalOpen(false)}
                    onSuccess={() => navigate(routes.HOME.handle.fullPath)}
                    serviceType={serviceType}
                />
            )}
        </div>
    )
}
